/**
 * UploadDialog — modal for uploading one or more PDFs to a calendar day.
 */
import React, { useState, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { calendarsApi } from './api';

const MAX_BYTES = 25 * 1024 * 1024;

function isPdf(f) {
  return f && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
}

export default function UploadDialog({ calendarId, dayId, onUploaded, onClose }) {
  const { token } = useAuth();
  const inputRef = useRef(null);
  // queue items: { id, file, label, status: 'pending'|'uploading'|'done'|'error'|'skipped', message }
  const [queue, setQueue] = useState([]);
  const queueRef = useRef([]);
  const setQueueBoth = (updater) => {
    setQueue((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      queueRef.current = next;
      return next;
    });
  };
  const [busy, setBusy] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);

  function addFiles(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const additions = files.map((f) => {
      let status = 'pending';
      let message = '';
      if (!isPdf(f)) { status = 'skipped'; message = 'Not a PDF'; }
      else if (f.size > MAX_BYTES) { status = 'skipped'; message = `${(f.size / 1024 / 1024).toFixed(1)} MB exceeds 25 MB limit`; }
      return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        label: f.name.replace(/\.pdf$/i, ''),
        status,
        message,
      };
    });
    setQueueBoth((prev) => [...prev, ...additions]);
    if (inputRef.current) inputRef.current.value = '';
  }

  function updateLabel(id, label) {
    setQueueBoth((prev) => prev.map((q) => q.id === id ? { ...q, label } : q));
  }

  function removeItem(id) {
    setQueueBoth((prev) => prev.filter((q) => q.id !== id));
  }

  async function uploadAll() {
    setBusy(true);
    const pendingIds = queueRef.current.filter((q) => q.status === 'pending').map((q) => q.id);
    for (const id of pendingIds) {
      setQueueBoth((prev) => prev.map((q) => q.id === id ? { ...q, status: 'uploading', message: '' } : q));
      const entry = queueRef.current.find((q) => q.id === id);
      if (!entry) continue;
      try {
        const fd = new FormData();
        fd.append('file', entry.file);
        fd.append('dayId', dayId);
        if (entry.label) fd.append('label', entry.label);
        const res = await calendarsApi.uploadPdf(calendarId, fd, token);
        onUploaded(res.item, res.warning);
        setUploadedCount((n) => n + 1);
        setQueueBoth((prev) => prev.map((q) => q.id === id ? { ...q, status: 'done', message: res.warning ? `Flagged: ${res.warning}` : 'Uploaded' } : q));
      } catch (err) {
        setQueueBoth((prev) => prev.map((q) => q.id === id ? { ...q, status: 'error', message: err.message || 'Upload failed' } : q));
      }
    }
    setBusy(false);
  }

  const pendingCount = queue.filter((q) => q.status === 'pending').length;
  const errorCount = queue.filter((q) => q.status === 'error').length;

  function statusBadge(q) {
    const base = { fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 4, marginLeft: 8 };
    if (q.status === 'done') return <span style={{ ...base, background: 'rgba(34,197,94,0.18)', color: '#15803d' }}>✓ Done</span>;
    if (q.status === 'uploading') return <span style={{ ...base, background: 'rgba(59,130,246,0.18)', color: '#1d4ed8' }}>Uploading…</span>;
    if (q.status === 'error') return <span style={{ ...base, background: 'rgba(239,68,68,0.18)', color: '#b91c1c' }}>Error</span>;
    if (q.status === 'skipped') return <span style={{ ...base, background: 'rgba(148,163,184,0.25)', color: 'var(--muted)' }}>Skipped</span>;
    return <span style={{ ...base, background: 'var(--surface-soft)', color: 'var(--muted)' }}>Pending</span>;
  }

  return (
    <div className="cal-modal-backdrop" onClick={onClose} role="presentation">
      <div className="cal-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Upload PDFs">
        <div className="cal-modal-head">
          <h3>
            Upload PDFs
            {uploadedCount > 0 && <span style={{ marginLeft: 8, fontSize: '0.85rem', color: 'var(--muted)', fontWeight: 500 }}>· {uploadedCount} uploaded</span>}
          </h3>
          <button type="button" className="cal-icon-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="cal-upload-form" style={{ padding: '0.85rem 1.1rem' }}>
          <p className="cal-upload-hint">Pick one or more PDFs (up to 25 MB each). They’ll be checked for safe content before being shown to followers.</p>
          <label className="tt-field">
            <span>Add files</span>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              onChange={(e) => addFiles(e.target.files)}
            />
          </label>

          {queue.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 0', display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '40vh', overflowY: 'auto' }}>
              {queue.map((q) => (
                <li key={q.id} style={{ border: '1px solid var(--chip-border)', borderRadius: 8, padding: '8px 10px', background: 'var(--surface-soft)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {q.file.name}{statusBadge(q)}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                        {(q.file.size / 1024 / 1024).toFixed(2)} MB
                        {q.message ? ` · ${q.message}` : ''}
                      </div>
                    </div>
                    {(q.status === 'pending' || q.status === 'skipped' || q.status === 'error') && !busy && (
                      <button type="button" className="cal-icon-btn" onClick={() => removeItem(q.id)} aria-label="Remove">×</button>
                    )}
                  </div>
                  {(q.status === 'pending' || q.status === 'uploading') && (
                    <input
                      type="text"
                      value={q.label}
                      onChange={(e) => updateLabel(q.id, e.target.value)}
                      placeholder="Display name"
                      maxLength={200}
                      disabled={busy}
                      style={{ width: '100%', marginTop: 6, fontSize: 13 }}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="cal-edit-actions" style={{ marginTop: '0.85rem' }}>
            <button type="button" className="secondary-btn" onClick={onClose} disabled={busy}>
              {uploadedCount > 0 ? 'Done' : 'Cancel'}
            </button>
            <button
              type="button"
              className="primary-btn"
              disabled={busy || pendingCount === 0}
              onClick={uploadAll}
            >
              {busy ? 'Uploading…' : pendingCount > 1 ? `Upload ${pendingCount} files` : 'Upload'}
            </button>
          </div>
          {errorCount > 0 && !busy && (
            <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
              {errorCount} file{errorCount === 1 ? '' : 's'} failed. Remove or re-add to retry.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
